import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  CircularProgressbar,
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

interface ExerciseTimerProps {
  duration: number; // in seconds
  onComplete: () => void;
  isPaused?: boolean;
  isRest?: boolean;
}

// Rest periods follow the progressive technique from the article (factor of 0.4 to 1.0 of exercise time)
const ExerciseTimer: React.FC<ExerciseTimerProps> = ({
  duration,
  onComplete,
  isPaused = false,
  isRest = false,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const [isRunning, setIsRunning] = useState(!isPaused);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startTimer = () => {
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
  };

  const resetTimer = () => {
    setTimeRemaining(duration);
    setIsRunning(false);
  };

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    if (isPaused) {
      setIsRunning(false);
    }
  }, [isPaused]);

  useEffect(() => {
    setTimeRemaining(duration);
  }, [duration]);

  useEffect(() => {
    if (isRunning && timeRemaining > 0) {
      timerRef.current = setTimeout(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      onComplete();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isRunning, timeRemaining, onComplete]);

  const progressPercentage = (timeRemaining / duration) * 100;
  const timerColor = isRest ? "#f59e0b" : "#2563eb"; // amber for rest, blue for exercise

  return (
    <div className="flex flex-col items-center">
      <div className="w-32 h-32 mb-3">
        <CircularProgressbarWithChildren
          value={progressPercentage}
          strokeWidth={8}
          styles={buildStyles({
            strokeLinecap: "round",
            pathColor: timerColor,
            trailColor: "#e2e8f0",
          })}
        >
          <div className="flex flex-col items-center">
            <span className="text-2xl font-bold">
              {formatTime(timeRemaining)}
            </span>
            <span className="text-xs mt-1">{isRest ? "Rest" : "Work"}</span>
          </div>
        </CircularProgressbarWithChildren>
      </div>

      <div className="flex gap-2 mt-2">
        {isRunning ? (
          <Button
            variant="outline"
            onClick={pauseTimer}
            size="sm"
            className="flex items-center"
          >
            <span className="material-icons text-sm mr-1">pause</span>
            Pause
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={startTimer}
            size="sm"
            className="flex items-center"
          >
            <span className="material-icons text-sm mr-1">play_arrow</span>
            Start
          </Button>
        )}
        <Button
          variant="outline"
          onClick={resetTimer}
          size="sm"
          className="flex items-center"
        >
          <span className="material-icons text-sm mr-1">refresh</span>
          Reset
        </Button>
      </div>
    </div>
  );
};

export default ExerciseTimer;
